import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
    Building2, Users, Plus, Pencil, Trash2, Power, Eye,
    Home, LogOut, Menu, X, Settings, Palette, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SuperAdminPage() {
    const navigate = useNavigate();
    const { token, logout, user } = useAuth();
    const [activeSection, setActiveSection] = useState('organizations');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Data states
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    
    // Modal states
    const [createOrgModal, setCreateOrgModal] = useState({ open: false });
    const [editOrgModal, setEditOrgModal] = useState({ open: false, org: null });
    const [viewOrgModal, setViewOrgModal] = useState({ open: false, org: null });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, org: null });
    
    // Form state for creating org
    const [newOrg, setNewOrg] = useState({
        name: '',
        slug: '',
        tagline: 'Premium Lifestyle Membership',
        type: 'residential',
        admin_email: '',
        admin_password: '',
        admin_name: ''
    });

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/super-admin/organizations`, { headers });
            setOrganizations(response.data);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to load organizations');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrg = async () => {
        if (!newOrg.name || !newOrg.slug || !newOrg.admin_email || !newOrg.admin_password || !newOrg.admin_name) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        // Validate slug format
        if (!/^[a-z0-9-]+$/.test(newOrg.slug)) {
            toast.error('Slug must contain only lowercase letters, numbers, and hyphens');
            return;
        }
        
        try {
            await axios.post(`${API}/super-admin/organizations`, newOrg, { headers });
            toast.success(`Organization "${newOrg.name}" created successfully`);
            setCreateOrgModal({ open: false });
            setNewOrg({
                name: '',
                slug: '',
                tagline: 'Premium Lifestyle Membership',
                type: 'residential',
                admin_email: '',
                admin_password: '',
                admin_name: ''
            });
            fetchOrganizations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create organization');
        }
    };

    const handleToggleActive = async (org) => {
        try {
            const response = await axios.put(`${API}/super-admin/organizations/${org.id}/toggle-active`, {}, { headers });
            toast.success(`Organization ${response.data.is_active ? 'activated' : 'deactivated'}`);
            fetchOrganizations();
        } catch (error) {
            toast.error('Failed to update organization status');
        }
    };

    const handleDeleteOrg = async () => {
        if (!deleteConfirm.org) return;
        
        try {
            await axios.delete(`${API}/super-admin/organizations/${deleteConfirm.org.id}`, { headers });
            toast.success(`Organization "${deleteConfirm.org.name}" deleted`);
            setDeleteConfirm({ open: false, org: null });
            fetchOrganizations();
        } catch (error) {
            toast.error('Failed to delete organization');
        }
    };

    const handleViewOrg = async (org) => {
        try {
            const response = await axios.get(`${API}/super-admin/organizations/${org.id}`, { headers });
            setViewOrgModal({ open: true, org: response.data });
        } catch (error) {
            toast.error('Failed to load organization details');
        }
    };

    const menuItems = [
        { id: 'organizations', icon: Building2, label: 'Organizations' },
    ];

    return (
        <div className="min-h-screen bg-gray-100" data-testid="super-admin-page">
            {/* Mobile Header */}
            <div className="lg:hidden bg-purple-900 text-white p-4 flex items-center justify-between sticky top-0 z-50">
                <button 
                    onClick={() => setSidebarOpen(true)}
                    className="p-2 hover:bg-purple-800 rounded-lg"
                >
                    <Menu className="w-6 h-6" />
                </button>
                <h1 className="text-lg font-bold">Super Admin Portal</h1>
                <button 
                    onClick={() => navigate('/')}
                    className="p-2 hover:bg-purple-800 rounded-lg"
                >
                    <Home className="w-6 h-6" />
                </button>
            </div>

            {/* Sidebar */}
            <aside className={`
                fixed inset-y-0 left-0 z-50 w-64 bg-purple-900 text-white transform transition-transform duration-200
                lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-bold">Residents Plus</h1>
                            <p className="text-purple-300 text-sm">Super Admin</p>
                        </div>
                        <button 
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden p-2 hover:bg-purple-800 rounded-lg"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <nav className="px-4 space-y-1">
                    {menuItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => {
                                setActiveSection(item.id);
                                setSidebarOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                                ${activeSection === item.id 
                                    ? 'bg-purple-800 text-white' 
                                    : 'text-purple-300 hover:bg-purple-800/50'}`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                        </button>
                    ))}
                </nav>

                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-purple-800">
                    <div className="text-sm text-purple-300 mb-3">
                        Logged in as: {user?.email}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/')}
                            className="flex-1 border-purple-700 text-purple-200 hover:bg-purple-800"
                        >
                            <Home className="w-4 h-4 mr-2" />
                            Home
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={logout}
                            className="flex-1 border-purple-700 text-purple-200 hover:bg-purple-800"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <main className="lg:ml-64 min-h-screen">
                <div className="p-6">
                    {/* Organizations Section */}
                    {activeSection === 'organizations' && (
                        <div>
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Organizations</h2>
                                    <p className="text-gray-600">Manage all registered organizations</p>
                                </div>
                                <Button
                                    onClick={() => setCreateOrgModal({ open: true })}
                                    className="bg-purple-600 hover:bg-purple-700"
                                    data-testid="create-org-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    New Organization
                                </Button>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-12">
                                    <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : organizations.length === 0 ? (
                                <div className="bg-white rounded-xl p-12 text-center">
                                    <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Organizations Yet</h3>
                                    <p className="text-gray-500 mb-4">Create your first organization to get started</p>
                                    <Button
                                        onClick={() => setCreateOrgModal({ open: true })}
                                        className="bg-purple-600 hover:bg-purple-700"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Create Organization
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid gap-4">
                                    {organizations.map(org => (
                                        <div 
                                            key={org.id}
                                            className={`bg-white rounded-xl p-6 shadow-sm border-l-4 ${org.is_active ? 'border-green-500' : 'border-gray-300'}`}
                                            data-testid={`org-card-${org.slug}`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-4">
                                                    {org.branding?.logo_url ? (
                                                        <img 
                                                            src={org.branding.logo_url} 
                                                            alt={org.name}
                                                            className="w-16 h-16 object-contain rounded-lg bg-gray-50"
                                                        />
                                                    ) : (
                                                        <div className="w-16 h-16 rounded-lg bg-purple-100 flex items-center justify-center">
                                                            <Building2 className="w-8 h-8 text-purple-600" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <h3 className="text-lg font-bold text-gray-900">{org.name}</h3>
                                                        <p className="text-gray-500">{org.tagline}</p>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="text-sm text-purple-600 font-medium">
                                                                {org.slug}.residentsplus.com
                                                            </span>
                                                            {/* Organization Type Badge */}
                                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                                org.type === 'residential'
                                                                    ? 'bg-stone-100 text-stone-700 border border-stone-300'
                                                                    : org.type === 'commercial'
                                                                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                                                    : 'bg-gray-100 text-gray-600 border border-gray-300'
                                                            }`} data-testid={`org-type-badge-${org.slug}`}>
                                                                {org.type === 'residential' ? 'Residential' : org.type === 'commercial' ? 'Commercial' : 'Unknown'}
                                                            </span>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                                org.is_active 
                                                                    ? 'bg-green-100 text-green-700'
                                                                    : 'bg-gray-100 text-gray-600'
                                                            }`}>
                                                                {org.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleViewOrg(org)}
                                                        data-testid={`view-org-${org.slug}`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleToggleActive(org)}
                                                        data-testid={`toggle-org-${org.slug}`}
                                                    >
                                                        {org.is_active ? (
                                                            <ToggleRight className="w-4 h-4 text-green-600" />
                                                        ) : (
                                                            <ToggleLeft className="w-4 h-4 text-gray-400" />
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setDeleteConfirm({ open: true, org })}
                                                        className="text-red-600 hover:bg-red-50"
                                                        data-testid={`delete-org-${org.slug}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* Membership Tiers Preview */}
                                            <div className="mt-4 pt-4 border-t border-gray-100">
                                                <div className="flex gap-4 flex-wrap">
                                                    {(org.membership_tiers || []).filter(t => t.is_active).map((tier, idx) => (
                                                        <div key={idx} className="text-sm">
                                                            <span className="font-medium text-gray-700">{tier.name}:</span>
                                                            <span className="text-gray-500 ml-1">£{tier.price}/yr ({tier.annual_credits} credits)</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>

            {/* Create Organization Modal */}
            <Dialog open={createOrgModal.open} onOpenChange={(open) => setCreateOrgModal({ open })}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create New Organization</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Organization Name *</Label>
                            <Input
                                value={newOrg.name}
                                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                                placeholder="e.g., Oakwood Village"
                                data-testid="org-name-input"
                            />
                        </div>
                        <div>
                            <Label>Slug (subdomain) *</Label>
                            <Input
                                value={newOrg.slug}
                                onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                                placeholder="e.g., oakwood-village"
                                data-testid="org-slug-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                URL will be: {newOrg.slug || 'your-slug'}.residentsplus.com
                            </p>
                        </div>
                        <div>
                            <Label>Organization Type *</Label>
                            <div className="flex gap-3 mt-2">
                                <button
                                    type="button"
                                    onClick={() => setNewOrg({ ...newOrg, type: 'residential' })}
                                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                                        newOrg.type === 'residential'
                                            ? 'border-stone-500 bg-stone-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    data-testid="org-type-residential"
                                >
                                    <div className={`text-sm font-medium ${newOrg.type === 'residential' ? 'text-stone-700' : 'text-gray-600'}`}>
                                        Residential
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">Retirement villages, care homes</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNewOrg({ ...newOrg, type: 'commercial' })}
                                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                                        newOrg.type === 'commercial'
                                            ? 'border-emerald-500 bg-emerald-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    data-testid="org-type-commercial"
                                >
                                    <div className={`text-sm font-medium ${newOrg.type === 'commercial' ? 'text-emerald-700' : 'text-gray-600'}`}>
                                        Commercial
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">Garden centres, retail venues</div>
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label>Tagline</Label>
                            <Input
                                value={newOrg.tagline}
                                onChange={(e) => setNewOrg({ ...newOrg, tagline: e.target.value })}
                                placeholder="e.g., Premium Lifestyle Membership"
                                data-testid="org-tagline-input"
                            />
                        </div>
                        <div className="border-t pt-4">
                            <h4 className="font-medium mb-3">Organization Admin Account</h4>
                            <div className="space-y-3">
                                <div>
                                    <Label>Admin Name *</Label>
                                    <Input
                                        value={newOrg.admin_name}
                                        onChange={(e) => setNewOrg({ ...newOrg, admin_name: e.target.value })}
                                        placeholder="e.g., John Smith"
                                        data-testid="admin-name-input"
                                    />
                                </div>
                                <div>
                                    <Label>Admin Email *</Label>
                                    <Input
                                        type="email"
                                        value={newOrg.admin_email}
                                        onChange={(e) => setNewOrg({ ...newOrg, admin_email: e.target.value })}
                                        placeholder="e.g., admin@oakwood-village.com"
                                        data-testid="admin-email-input"
                                    />
                                </div>
                                <div>
                                    <Label>Admin Password *</Label>
                                    <Input
                                        type="password"
                                        value={newOrg.admin_password}
                                        onChange={(e) => setNewOrg({ ...newOrg, admin_password: e.target.value })}
                                        placeholder="Strong password"
                                        data-testid="admin-password-input"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOrgModal({ open: false })}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCreateOrg}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="submit-create-org"
                        >
                            Create Organization
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View Organization Modal */}
            <Dialog open={viewOrgModal.open} onOpenChange={(open) => setViewOrgModal({ open, org: null })}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Organization Details</DialogTitle>
                    </DialogHeader>
                    {viewOrgModal.org && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                {viewOrgModal.org.branding?.logo_url ? (
                                    <img 
                                        src={viewOrgModal.org.branding.logo_url} 
                                        alt={viewOrgModal.org.name}
                                        className="w-20 h-20 object-contain rounded-lg bg-gray-50"
                                    />
                                ) : (
                                    <div className="w-20 h-20 rounded-lg bg-purple-100 flex items-center justify-center">
                                        <Building2 className="w-10 h-10 text-purple-600" />
                                    </div>
                                )}
                                <div>
                                    <h3 className="text-xl font-bold">{viewOrgModal.org.name}</h3>
                                    <p className="text-gray-500">{viewOrgModal.org.tagline}</p>
                                    <p className="text-purple-600 text-sm">{viewOrgModal.org.slug}.residentsplus.com</p>
                                </div>
                            </div>

                            {/* Stats */}
                            {viewOrgModal.org.stats && (
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-blue-600">{viewOrgModal.org.stats.member_count}</div>
                                        <div className="text-sm text-blue-800">Members</div>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-green-600">{viewOrgModal.org.stats.admin_count}</div>
                                        <div className="text-sm text-green-800">Admins</div>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-purple-600">{viewOrgModal.org.stats.manager_count}</div>
                                        <div className="text-sm text-purple-800">Managers</div>
                                    </div>
                                </div>
                            )}

                            {/* Membership Tiers */}
                            <div>
                                <h4 className="font-semibold mb-2">Membership Tiers</h4>
                                <div className="space-y-2">
                                    {(viewOrgModal.org.membership_tiers || []).map((tier, idx) => (
                                        <div key={idx} className={`p-3 rounded-lg ${tier.is_active ? 'bg-green-50' : 'bg-gray-50'}`}>
                                            <div className="flex justify-between items-center">
                                                <span className="font-medium">{tier.name}</span>
                                                <span className="text-sm">
                                                    £{tier.price}/year • {tier.annual_credits} credits
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500">{tier.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Branding */}
                            <div>
                                <h4 className="font-semibold mb-2">Branding</h4>
                                <div className="flex gap-4">
                                    <div className="flex items-center gap-2">
                                        <div 
                                            className="w-8 h-8 rounded-full border"
                                            style={{ backgroundColor: `hsl(${viewOrgModal.org.branding?.primary_color || '163 68% 18%'})` }}
                                        />
                                        <span className="text-sm">Primary</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div 
                                            className="w-8 h-8 rounded-full border"
                                            style={{ backgroundColor: `hsl(${viewOrgModal.org.branding?.secondary_color || '160 55% 27%'})` }}
                                        />
                                        <span className="text-sm">Secondary</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div 
                                            className="w-8 h-8 rounded-full border"
                                            style={{ backgroundColor: `hsl(${viewOrgModal.org.branding?.accent_color || '4 68% 68%'})` }}
                                        />
                                        <span className="text-sm">Accent</span>
                                    </div>
                                </div>
                            </div>

                            {/* Features */}
                            <div>
                                <h4 className="font-semibold mb-2">Enabled Features</h4>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(viewOrgModal.org.features || {}).map(([feature, enabled]) => (
                                        <span 
                                            key={feature}
                                            className={`px-3 py-1 rounded-full text-sm ${
                                                enabled 
                                                    ? 'bg-green-100 text-green-700' 
                                                    : 'bg-gray-100 text-gray-500 line-through'
                                            }`}
                                        >
                                            {feature.replace(/_/g, ' ')}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Contact */}
                            {viewOrgModal.org.contact && Object.values(viewOrgModal.org.contact).some(v => v) && (
                                <div>
                                    <h4 className="font-semibold mb-2">Contact Information</h4>
                                    <div className="text-sm space-y-1 text-gray-600">
                                        {viewOrgModal.org.contact.address && <p>{viewOrgModal.org.contact.address}</p>}
                                        {viewOrgModal.org.contact.phone && <p>Phone: {viewOrgModal.org.contact.phone}</p>}
                                        {viewOrgModal.org.contact.email && <p>Email: {viewOrgModal.org.contact.email}</p>}
                                        {viewOrgModal.org.contact.website && <p>Website: {viewOrgModal.org.contact.website}</p>}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm({ open, org: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete Organization</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-gray-700">
                            Are you sure you want to delete <strong>{deleteConfirm.org?.name}</strong>?
                        </p>
                        <p className="text-red-600 text-sm mt-2">
                            This will permanently delete all organization data including:
                        </p>
                        <ul className="text-sm text-gray-600 mt-2 list-disc list-inside">
                            <li>All user accounts</li>
                            <li>All activities and bookings</li>
                            <li>All events, offers, and tips</li>
                            <li>All credit transactions</li>
                        </ul>
                        <p className="text-red-600 text-sm mt-2 font-bold">This action cannot be undone.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, org: null })}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleDeleteOrg}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete-org"
                        >
                            Delete Organization
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default SuperAdminPage;
